/**
 * The sidebar tree's "Show more" pages. Each level's FIRST page lives in the
 * shared TanStack cache (the root query, one children query per expanded
 * folder); the pages "Show more" loads after it live here, per level, and are
 * appended to it when the tree flattens.
 *
 * Loaded pages are tied to the first page they continue (`from`, that page's
 * `nextCursor`). When a level refetches and its first page ends somewhere
 * else, the loaded pages no longer continue it and are dropped, so the level
 * offers "Show more" from its new first page instead of duplicating or
 * skipping rows. For the same reason a page is appended only while the
 * level's current cursor is still the one it was requested with.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { HierarchyNode } from '@bower/shared/types/api';

import { apiService } from '../../../services/api.service';
import {
  getHierarchyChildrenPage,
  type HierarchyChildrenPage,
} from '../../../services/api/hierarchy.api';
import { useAppToast } from '../../../hooks/ui/useAppToast';

/** A level's key: the parent folder's id, or ROOT_LEVEL for the workspace root. */
export const ROOT_LEVEL = 'root';

/** "Show more" at the end of a level that still has a next page. */
export interface ShowMoreEntry {
  readonly kind: 'more';
  /** `ROOT_LEVEL` or the parent folder's id. */
  readonly levelKey: string;
  /** 1-based, the level of the rows it follows. */
  readonly level: number;
  readonly cursor: string;
}

export type Level = {
  readonly nodes: ReadonlyArray<HierarchyNode>;
  readonly nextCursor: string | null;
};

interface LoadedPages {
  /** The first page's `nextCursor` these pages continue. */
  readonly from: string;
  readonly nodes: ReadonlyArray<HierarchyNode>;
  readonly nextCursor: string | null;
}

const EMPTY: Level = { nodes: [], nextCursor: null };

function joined(first: Level, loaded: LoadedPages | undefined): Level {
  if (!loaded || loaded.from !== first.nextCursor) return first;
  return { nodes: [...first.nodes, ...loaded.nodes], nextCursor: loaded.nextCursor };
}

export function useLevelPages(
  rootPage: HierarchyChildrenPage | undefined,
  firstPages: ReadonlyMap<string, HierarchyChildrenPage>,
) {
  const toast = useAppToast();
  const [loaded, setLoaded] = useState<ReadonlyMap<string, LoadedPages>>(new Map());
  const [loadingMore, setLoadingMore] = useState<ReadonlySet<string>>(new Set());

  const levels = useMemo(() => {
    const byFolder = new Map<string, Level>();
    firstPages.forEach((page, id) => byFolder.set(id, joined(page, loaded.get(id))));
    return { root: joined(rootPage ?? EMPTY, loaded.get(ROOT_LEVEL)), byFolder };
  }, [rootPage, firstPages, loaded]);

  // Read after a request resolves, so the append checks the committed levels.
  // `loadedRef` also takes each append at once, so two pages landing in one
  // tick both see the other.
  const firstCursorsRef = useRef(new Map<string, string | null>());
  const loadedRef = useRef(loaded);
  useEffect(() => {
    firstCursorsRef.current = new Map([
      [ROOT_LEVEL, rootPage?.nextCursor ?? null],
      ...[...firstPages].map(([id, page]) => [id, page.nextCursor] as const),
    ]);
    loadedRef.current = loaded;
  }, [rootPage, firstPages, loaded]);

  const append = useCallback((levelKey: string, cursor: string, page: HierarchyChildrenPage) => {
    const first = firstCursorsRef.current.get(levelKey) ?? null;
    const current = loadedRef.current.get(levelKey);
    const continues = current !== undefined && current.from === first;
    if (first === null || (continues ? current.nextCursor : first) !== cursor) return false;
    const next = new Map(loadedRef.current).set(levelKey, {
      from: first,
      nodes: [...(continues ? current.nodes : []), ...page.nodes],
      nextCursor: page.nextCursor,
    });
    loadedRef.current = next;
    setLoaded(next);
    return true;
  }, []);

  /**
   * Load the level's next page and append it. Resolves to the first appended
   * row's id (the tree moves focus there), or null when nothing was appended
   * (stale, empty or failed).
   */
  const showMore = useCallback(
    async ({ levelKey, cursor }: ShowMoreEntry): Promise<string | null> => {
      setLoadingMore((prev) => new Set(prev).add(levelKey));
      try {
        const page = await getHierarchyChildrenPage(
          apiService,
          levelKey === ROOT_LEVEL
            ? { parentType: 'workspace', cursor }
            : { parentType: 'project', parentId: levelKey, cursor },
        );
        return append(levelKey, cursor, page) ? (page.nodes[0]?.id ?? null) : null;
      } catch (error) {
        console.error('Failed to fetch more children:', error);
        toast({ title: 'Could not load more items', status: 'error' });
        return null;
      } finally {
        setLoadingMore((prev) => {
          const next = new Set(prev);
          next.delete(levelKey);
          return next;
        });
      }
    },
    [append, toast],
  );

  return { ...levels, loadingMore, showMore };
}
